import { adjustContentVisibility, SectionParagraph as Subtitle } from '@/components/shared/Content';
import { useElementLerp } from '@/hooks/useElementLerp';
import { useIsClient } from '@/hooks/useIsClient';
import usePrefersReducedMotion from '@/hooks/usePrefersReducedMotion';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import { easeInQuad, Flex, isFirefox, useResponsiveValue } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import Image from 'next/image';
import React, { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { mergeRefs } from 'react-merge-refs';
import styled, { css } from 'styled-components';
import CTA from './CTA';
import { CarPageContext } from './CarPageContext';
import { Section } from './StyledElements';
import cloudsUrl from './assets/footer/clouds.svg';
import layer1Url from './assets/footer/layer-1.svg';
import layer2Url from './assets/footer/layer-2.svg';
import layer3Url from './assets/footer/layer-3.svg';
import layer4Url from './assets/footer/layer-4.svg';
import layer5Url from './assets/footer/layer-5.svg';
import layer6Url from './assets/footer/layer-6.svg';

const transition = css`
  transition: transform 0.3s ease-out;
`;

const Container = styled(Section)`
  position: relative;
  perspective: 100px;
  transform-style: preserve-3d;
  transform: translateY(5px);
  ${adjustContentVisibility};
`;

const Clouds = styled(Image).attrs({
  src: cloudsUrl.src,
  width: 2044,
  height: 466,
  alt: 'Clouds',
})`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  ${transition}

  ${MEDIA_QUERIES.mobile`
        left: 0%;
        height: 260px;
    `}
`;

const Layer = styled(Image).attrs({
  width: 2670,
  height: 650,
  alt: 'Car insurance starting at $30 a month based on how you drive',
})`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  transform-origin: bottom center;
  width: 2670px;
  height: 650px;
  ${transition}

  ${MEDIA_QUERIES.mobile`
        left: 0%;
        height: 260px;
    `}
`;

const Layer1 = styled(Layer).attrs({
  src: layer1Url.src,
})``;

const Layer2 = styled(Layer).attrs({
  src: layer2Url.src,
})``;

const Layer3 = styled(Layer).attrs({
  src: layer3Url.src,
})``;

const Layer4 = styled(Layer).attrs({
  src: layer4Url.src,
})``;

const Layer5 = styled(Layer).attrs({
  src: layer5Url.src,
})``;

const Layer6 = styled(Layer).attrs({
  src: layer6Url.src,
})``;

const StyledCTA = styled(CTA)`
  align-self: center;
`;

const TitleContainer = styled(Flex)`
  ${transition}
`;

interface FooterProps {
  t?: number;
  ctaLabel?: string;
  ctaUrl?: string;
  underCtaText?: string;
}

const Footer: React.FC<FooterProps> = ({
  t,
  ctaLabel = 'Check our prices',
  ctaUrl,
  underCtaText = 'As low as $30/mo, based on your driving profile',
}) => {
  const { absoluteScroll } = useSmoothScroll();
  const isClient = useIsClient();
  const { showAdditionalHeaderCTA } = useShowHeaderCtaOnScroll();
  const { ref: btnRef, inView } = useInView();
  const { isEmbeddedPage } = useContext(CarPageContext);
  const prefersReducedMotion = usePrefersReducedMotion();
  const ctaTranslateY = useResponsiveValue([100, 200, 300]);
  const sceneTranslateX = useResponsiveValue([200, 100, 0]);
  const [viewed, setViewed] = React.useState(false);

  const [ref, newT] = useElementLerp(t ? t : absoluteScroll, {
    startPoint: 0.6,
    range: [0, 0.7],
  });

  useEffect(() => {
    if (inView) {
      setViewed(true);
    }

    if (viewed) {
      showAdditionalHeaderCTA(!inView);
    }
  }, [inView, showAdditionalHeaderCTA, viewed]);

  const reversedT = isFirefox() || prefersReducedMotion ? 0 : easeInQuad(1 - newT);
  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'footer',
    page: 'car',
  });

  return (
    <Container
      ref={mergeRefs([ref, intersectionRef])}
      mb={0}
      pt={[45, 95, 95, 95]}
      width={1}
      height={[520, 894, 920]}
      position="relative"
      overflow="hidden"
      contain="paint"
    >
      <Clouds
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${reversedT * 300 - 100}px)`,
        }}
      />
      <Layer1
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${reversedT * 1000}px)`,
        }}
      />
      <Layer2
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${reversedT * 600}px)`,
        }}
      />
      <Layer3
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${reversedT * 400}px)`,
        }}
      />
      <Layer4
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${reversedT * 200}px)`,
        }}
      />
      {isClient && (
        <Layer5
          style={{
            transform: `translateX(-50%) translateX(${sceneTranslateX}px)`,
          }}
        />
      )}
      <Layer6
        style={{
          transform: `translateX(-50%) translateX(${
            sceneTranslateX + reversedT * -600
          }px) translateY(${reversedT * 200}px)`,
        }}
      />
      <TitleContainer
        flexDirection="column"
        style={{
          transform: `translateY(${newT * ctaTranslateY}px)`,
        }}
      >
        {!isEmbeddedPage && (
          <Flex gap="20px" flexDirection="column" ref={btnRef}>
            <StyledCTA aria-label={`${ctaLabel} ${underCtaText}`} customUrl={ctaUrl}>
              {ctaLabel}
            </StyledCTA>
            <Subtitle>{underCtaText}</Subtitle>
          </Flex>
        )}
      </TitleContainer>
    </Container>
  );
};

export default React.memo(Footer);
